import React from "react";

import campaignStepOneIcon from "../assets/images/svg/mab-compgn-4.svg";
import dropOpenIcon from "../assets/images/svg/mab-drop-open-4.svg";
import downArrow from "../assets/images/svg/mab-down-arrow.svg";
import ellipseBg from "../assets/images/svg/mab-ellipse.svg";
import landingFrame from "../assets/images/svg/landing-frame.svg";
import landingMobileFrame from "../assets/images/svg/landing-frame-mobile.svg";
import NextButton from "./common/next-button";

type LandingDropdownProps = {
  id: string;
  expandedId: string | null;
  onToggle: (id: string) => void;
  onNext?: (id: string) => void;
  landingFileName: string;
  onLandingUploadChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  isLandingUploading?: boolean;
  landingUploadProgress?: number;
  onLandingRemove?: () => void;
  isEditMode?: boolean;
};

const LandingDropdown: React.FC<LandingDropdownProps> = ({
  id,
  expandedId,
  onToggle,
  onNext,
  landingFileName,
  onLandingUploadChange,
  isLandingUploading,
  landingUploadProgress,
  onLandingRemove,
  isEditMode = false,
}) => {
  const isOpen = expandedId === id;
  const fileInputRef = React.useRef<HTMLInputElement>(null);
  const allowedImageTypes = React.useMemo(
    () => new Set(["image/png", "image/jpeg", "image/webp"]),
    [],
  );

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (isEditMode) return;
    if (e.target.files && e.target.files.length > 0) {
      const file = e.target.files[0];
      const fileName = (file.name || "").toLowerCase();
      const isSvg =
        file.type === "image/svg+xml" || fileName.endsWith(".svg");
      const isAllowedType =
        allowedImageTypes.has(file.type) ||
        (file.type === "" &&
          (fileName.endsWith(".png") ||
            fileName.endsWith(".jpg") ||
            fileName.endsWith(".jpeg") ||
            fileName.endsWith(".webp")));

      if (isSvg || !isAllowedType) {
        alert("Please upload a PNG, JPG/JPEG, or WEBP image (SVG is not supported).");
        if (fileInputRef.current) {
          fileInputRef.current.value = "";
        }
        return;
      }

      const img = new Image();
      const objectUrl = URL.createObjectURL(file);

      img.onload = () => {
        const aspectRatio = img.width / img.height;
        const isSquare = Math.abs(aspectRatio - 1) < 0.01;

        URL.revokeObjectURL(objectUrl);

        if (!isSquare) {
          alert(
            "Please upload an image with a 1:1 aspect ratio (square image).",
          );
          if (fileInputRef.current) {
            fileInputRef.current.value = "";
          }
          return;
        }

        onLandingUploadChange(e);

        if (fileInputRef.current) {
          fileInputRef.current.value = "";
        }
      };

      img.onerror = () => {
        URL.revokeObjectURL(objectUrl);
        alert("Failed to load image. Please try again.");
        if (fileInputRef.current) {
          fileInputRef.current.value = "";
        }
      };

      img.src = objectUrl;
    }
  };

  return (
    <div className="w-full">
      <div
        className="relative rounded-[12px] sm:rounded-[16px] md:rounded-[20px] shadow-[0_14px_40px_rgba(0,0,0,0.25)] overflow-hidden w-full"
        style={{
          background: "#878DA11A",
          backgroundImage: `url(${ellipseBg})`,
          backgroundSize: "100% 100%",
          backgroundPosition: "center",
          backgroundRepeat: "no-repeat",
        }}
      >
        {/* HEADER */}
        <div
          className="flex items-center gap-2 sm:gap-3 cursor-pointer hover:opacity-90 transition-opacity min-h-[60px] h-[60px] sm:min-h-[72px] sm:h-[72px] md:min-h-[88px] md:h-[88px] px-4 py-4 sm:px-4 sm:py-4 md:px-6 md:py-6"
          onClick={() => onToggle(id)}
        >
          <div className="flex items-center gap-2 sm:gap-3 flex-1 min-w-0">
            <img
              src={isOpen ? dropOpenIcon : campaignStepOneIcon}
              alt="Landing Page"
              className="w-7 h-7 sm:w-8 sm:h-8 flex-shrink-0"
            />
            <span
              className="text-[16px] whitespace-nowrap"
              style={{
                fontFamily: "Poppins",
                fontWeight: 400,
                lineHeight: "130%",
                color: isOpen ? "#58F2A9" : "#FFFFFF",
              }}
            >
              Landing Page Artwork
            </span>
          </div>
          <img
            src={downArrow}
            alt="Toggle"
            className="pointer-events-none transition-transform flex-shrink-0"
            style={{
              height: "9.5px",
              width: "auto",
              transform: isOpen ? "rotate(180deg)" : "rotate(0deg)",
              filter: isOpen
                ? "brightness(0) saturate(100%) invert(78%) sepia(89%) saturate(1352%) hue-rotate(90deg) brightness(102%) contrast(101%)"
                : "none",
            }}
          />
        </div>

        {/* BODY */}
        {isOpen && (
          <div className="flex flex-col gap-3 sm:gap-4 md:gap-6 px-4 pb-4 sm:px-4 sm:pb-4 md:px-6 md:pb-6 md:ml-[41px]">
            {/* Title & subtitle */}
            <div className="flex flex-col gap-1">
              <span className="text-white text-[14px] lg:text-[16px] font-normal leading-[130%]">
                Customize the main landing page’s artwork
              </span>
              {/* <span className="text-[#8FA5BF] text-xs md:text-[16px] font-medium font-[Poppins] leading-[150%]">
                Customize the main landing page's image and background. Image
                must be 1:1 ratio (square).
              </span> */}
            </div>

            {/* Drop zone - only when no file selected */}
            {!landingFileName && !isEditMode && (
              <>
                {/* ── MOBILE drop zone (landingMobileFrame SVG as border) ── */}
                <label
                  htmlFor="landing-upload"
                  className="relative w-full flex flex-col items-center justify-center cursor-pointer rounded-[14px] md:hidden mb-3"
                  style={{
                    minHeight: "200px",
                    backgroundImage: `url(${landingMobileFrame})`,
                    backgroundSize: "100% 100%",
                    backgroundRepeat: "no-repeat",
                    backgroundPosition: "center",
                  }}
                ></label>

                {/* ── DESKTOP drop zone (image frame) ── */}
                <label
                  htmlFor="landing-upload"
                  className="relative w-full min-h-[200px] md:min-h-[260px] hidden md:flex cursor-pointer rounded-[14px] overflow-hidden"
                  style={{
                    backgroundImage: `url(${landingFrame})`,
                    backgroundSize: "100% 100%",
                    backgroundRepeat: "no-repeat",
                    backgroundPosition: "center",
                  }}
                />
              </>
            )}

            <input
              ref={fileInputRef}
              id="landing-upload"
              type="file"
              accept="image/png,image/jpeg,image/webp"
              className="hidden"
              disabled={isEditMode}
              onChange={handleFileChange}
            />

            {/* UPLOAD STATUS */}
            {(isLandingUploading || landingFileName) && (
              <div className="w-full rounded-[14px] bg-[#121420] border border-[#2A2F3D] p-3 flex flex-col gap-3">
                <div className="flex items-center justify-between gap-2">
                  <div className="text-white text-sm font-medium [font-family:'Poppins',Helvetica]">
                    {isLandingUploading
                      ? "Uploading..."
                      : "Uploaded landing file"}
                  </div>
                  {onLandingRemove && !isEditMode && (
                    <button
                      type="button"
                      onClick={onLandingRemove}
                      className="text-xs text-[#EE6161] hover:underline font-medium"
                    >
                      Remove
                    </button>
                  )}
                </div>

                {isLandingUploading && (
                  <div className="w-full h-2 rounded-full bg-[#1E2433] overflow-hidden">
                    <div
                      className="h-full bg-[#58F2A9] transition-all duration-200"
                      style={{ width: `${landingUploadProgress ?? 0}%` }}
                    />
                  </div>
                )}

                {!isLandingUploading && landingFileName && (
                  <span className="text-[#58F2A9] text-xs [font-family:'Poppins',Helvetica]">
                    ✓ Successfully uploaded artwork!
                  </span>
                )}

                {landingFileName && (
                  <div className="text-[#878DA1] text-xs [font-family:'Poppins',Helvetica] break-all">
                    {landingFileName}
                  </div>
                )}
              </div>
            )}

            {/* NEXT BUTTON */}
            <div className="flex w-full justify-end">
              <NextButton
                size="sm"
                className="w-full md:w-auto"
                onClick={() => onNext && onNext(id)}
              />
            </div>
            {isEditMode && (
              <span className="text-[#8FA5BF] text-xs font-[Poppins]">
                Landing page asset is locked in edit mode.
              </span>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default LandingDropdown;
